import { useLocation } from 'react-router-dom';

import { matchesAnyPathname, normalizePathname } from '@/shared/lib/utils/pathname';

/**
 * C28(planner docs-ops ddf04f0, 팀장 2026-10-18) — 앱의 «보기» 웹뷰(가입 ① 포함)가 이 문서 페이지를 띄우면 사이트 머리글·«로그인»·
 * 시세 띠·하단 탭·푸터·쿠키 배너까지 같이 보였다(가입 중에 «로그인» 버튼이 보인다). `?embed=1` 이면 본문만 그린다.
 *
 * 🔸 이 세 문서 페이지에서만 켠다 — 다른 주소의 `embed=1` 은 무시한다(머리글이 사라진 채 사이트를 돌아다니는 상태 방지).
 * 🔸 끝 슬래시는 무시한다(`/privacy-policy/?embed=1` 도 embed). nginx 는 이 경로들에서 슬래시 리다이렉트를 하지 않는다
 *    (nginx.conf·nginx.prod.conf `location /` 의 try_files 에 `$uri/` 가 없음 — 2026-10-18 dev·운영 실측 200·Location 없음) → 쿼리가 살아남는다.
 */
export const EMBED_PATHS = ['/terms', '/privacy-policy', '/house-rule'] as const;

/**
 * TRAD-3867(android 10/30 실측) — 인사이트 목록·상세도 앱 웹뷰가 띄운다(`/insights?embed=1#map` · `/insights/{slug}?src=app&embed=1&theme=`).
 * 목록 `/insights` 와 **한 단계 아래**(`/insights/<slug>`)만 — 더 깊은 경로·다른 섹션은 종전대로 무시. 끝 슬래시는 normalize 뒤 비교.
 * 🔴 index.html 인라인 스크립트에 같은 정규식이 한 번 더 있다(embed-static-c28.test.ts 가 원문 대조).
 */
export const EMBED_PATH_PATTERN_SOURCE = '^\\/insights(\\/[^\\/]+)?$';
const EMBED_PATH_PATTERN = new RegExp(EMBED_PATH_PATTERN_SOURCE);

export const isEmbedPath = (pathname: string): boolean =>
  matchesAnyPathname(pathname, EMBED_PATHS) || EMBED_PATH_PATTERN.test(normalizePathname(pathname));

export const isEmbedView = (pathname: string, search: string): boolean =>
  new URLSearchParams(search).get('embed') === '1' && isEmbedPath(pathname);

/*
 * 🔸 «페이지 안 내부 링크의 embed 유지»(팀장 조건): 세 페이지의 내부 이동은 처리방침 목차 `#앵커`(쿼리 유지)와 약관 «뒤로»(history)뿐이고,
 *    사이트로 나가는 처리방침 «홈» 링크는 임베드에서 숨긴다 — ~~그래서 embed 를 덧붙일 링크가 지금은 없다(헬퍼를 두지 않았다).~~
 *    🔸 TRAD-3867(10/30) — 인사이트는 섹션 «안» 링크(목록 카드 → 상세 · 카테고리 라벨 → 목록 필터 · «다른 글»)가 있어 `withEmbedParams` 를 둔다.
 *    섹션 «밖»으로 나가는 링크(경제 지표·설치 버튼)는 임베드에서 숨긴다(종전 원칙 그대로).
 *    약관 본문(서버 HTML) 안의 링크는 서버 문서가 정한다 — 이 화면이 고치지 않는다.
 */
const EMBED_CARRY_PARAMS = ['embed', 'theme', 'src'] as const;

/**
 * 지금 주소가 임베드(`embed=1`)면 `embed`·`theme`·`src` 를 `to` 에 이어 붙인다(이미 있는 쿼리·해시는 보존). 임베드가 아니면 `to` 그대로.
 * 순수 함수 — 판정은 쿼리만 본다(호출하는 쪽이 이미 임베드 경로 안의 화면이다).
 */
export const withEmbedParams = (to: string, currentSearch: string): string => {
  const current = new URLSearchParams(currentSearch);
  if (current.get('embed') !== '1') return to;
  const hashAt = to.indexOf('#');
  const beforeHash = hashAt === -1 ? to : to.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : to.slice(hashAt);
  const queryAt = beforeHash.indexOf('?');
  const path = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? '' : beforeHash.slice(queryAt + 1));
  for (const key of EMBED_CARRY_PARAMS) {
    const v = current.get(key);
    if (v !== null && !params.has(key)) params.set(key, v);
  }
  return `${path}?${params.toString()}${hash}`;
};
export const useEmbedView = (): boolean => {
  const { pathname, search } = useLocation();
  return isEmbedView(pathname, search);
};

/**
 * 임베드 테마(팀장 2026-10-18, 계약: 쿼리 `theme`, 값 `light`|`dark` 소문자). 앱 «보기» 웹뷰가 앱 테마를 그대로 넘긴다.
 * 임베드일 때만 존중한다(일반 화면은 무시) · 그 밖의 값(system·대문자 등)은 null(→ 종전 규칙) · 저장하지 않는다(사이트 기본 설정을 바꾸지 않음).
 * 🔸 종전 규칙: 저장값이 없으면 다크(ThemeProvider) — 기기 설정(prefers-color-scheme)은 보지 않는다.
 */
export type EmbedTheme = 'light' | 'dark';

export const embedThemeOverride = (pathname: string, search: string): EmbedTheme | null => {
  if (!isEmbedView(pathname, search)) return null;
  const v = new URLSearchParams(search).get('theme');
  return v === 'light' || v === 'dark' ? v : null;
};

/** ThemeProvider 초기값 — 임베드 테마가 있으면 그것, 아니면 저장값(없으면 다크). */
export const resolveInitialDark = (saved: string | null, pathname: string, search: string): boolean => {
  const override = embedThemeOverride(pathname, search);
  if (override) return override === 'dark';
  return saved === null ? true : saved === 'true';
};
